import { WebContentFormatEnum } from '../shared/enum/web-content-format.enum';
import { LazyLoadEvent, ConfirmationService, SelectItem } from 'primeng/api';
import { WebContentDto } from '../shared/dto/web-content.dto';
import { PagedResultGeneric, DatagridService, TranslationService, NotificationService, AuthService } from 'src/app/shared';
import { WebContentSearchDto } from 'src/app/web-content/shared/dto/web-content-search.dto';
import { WebContentService } from '../shared/web-content.service';
import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { WebContentTypeService } from '../shared/web-content-type.service';
import { timeoutWrapper } from 'src/app/shared/decorator/timeout-wrapper';
import { LazySelectableTableComponent } from 'src/app/shared/primeng/abstract-classes/lazy-selectable-table.component';
import { debounce } from 'lodash-decorators';
import { WebContentType } from 'src/app/shared/model/webcontenttype.model';
import { WebContentServiceService } from '../shared/web-content-service.service';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';

@Component({
  selector: 'wl-web-content-search-grid',
  templateUrl: './web-content-search-grid.component.html',
  styleUrls: ['./web-content-search-grid.component.less'],
    standalone: false
})
export class WebContentSearchGridComponent extends LazySelectableTableComponent<WebContentSearchDto, WebContentDto> implements OnInit {
  @Input() includeProfile = true;
  @Input() includeHeader = true;
  @Input() isReadOnly = true;

  webContentSearchResult: PagedResultGeneric<WebContentDto>;
  webContentTypes: WebContentType[] = [];
  webContentFormats: SelectItem[] = [];
  webContentFormatEnum = WebContentFormatEnum;
  loading = false;
  safeToSearch = true;
  webContentMemberPortalUrl: string;

  constructor(
    private _webContentService: WebContentService,
    private _webContentTypeService: WebContentTypeService,
    private _webContentServiceService: WebContentServiceService,
    private _confirmationService: ConfirmationService,
    private _authService: AuthService,
    private _notifyService: NotificationService,
    private _translationService: TranslationService,
    private _navigationService: NavigationService,
    public datagridService: DatagridService
  ) {
    super('WebContentID', datagridService);
    this.searchParams = new WebContentSearchDto();
  }

  ngOnInit(): void {
    this._webContentTypeService.getWebContentTypes().then((typesResult) => {
      this.webContentTypes = typesResult.Result;
    });

    this._webContentService.getWebContentFormats().then((formats) => {
      this.webContentFormats = formats.map<SelectItem>((format) => {
        return { label: format, value: format };
      });
    });

    this.webContentMemberPortalUrl = this._navigationService.getLegacyDomain() + '/atlas/web-content/';
  }

  fetchResults(): Promise<PagedResultGeneric<WebContentDto>> {
    return this._webContentService.search(this.searchParams);
  }

  @debounce(500)
  filterChanged() {
    this.searchParams.PageNumber = 1;
    this.getWebContents();
  }

  public getWebContents() {
    if (!this.safeToSearch && this.searchParams.TotalCount > 1000) {
      this._notifyService.error(this._translationService.getInstant('profiles.search.page-size.warning'));
      return;
    }
    this.loading = true;
    this.fetchResults().then((result) => {
      if (this.searchParams.PageSize > 1000 && result.TotalCount > 1000) {
        this._notifyService.error(this._translationService.getInstant('profiles.search.page-size.warning'));
        this.loading = false;
        return;
      }
      this._webContentTypeService.getWebContentTypes().then((typesResult) => {
        result.Result?.forEach((webContent) => {
          typesResult.Result?.forEach((type) => {
            if (webContent.ContentTypeID === type.ContentTypeID) {
              webContent.WebContentType = type;
            }
          });
        });
      });
      this.searchParams.TotalCount = result.TotalCount;
      this.webContentSearchResult = result;
      this.loading = false;
      this.safeToSearch = true;
    });
  }

  confirmDeletion(webContent: WebContentDto) {
    this._confirmationService.confirm({
      message: this._translationService.getInstant('web-content.search-grid.delete-confirmation-message'),
      accept: () => {
        this.deleteWebContent(webContent);
      },
      reject: () => {
        this._confirmationService.close();
      },
    });
  }

  @timeoutWrapper()
  lazyLoadWebContents(event: LazyLoadEvent) {
    this.datagridService.mapDatagridPagingToPagedSearch(event, this.searchParams);
    this.getWebContents();
  }

  navigateToPortalWebcontent(contentId: number) {
    const portalUrl = this._authService.getPortalUrlWithAccessTokenHash(`/atlas/web-content/${contentId}`);
    this._navigationService.popupSafeNavigate(portalUrl);
  }

  private deleteWebContent(webContent: WebContentDto) {
    this.loading = true;
    this._webContentServiceService.deleteWebContent(webContent.ContentID).then(() => {
      this.getWebContents();
    });
  }

  pageSizeChanged() {
    this.searchParams.PageNumber = 1;
    this.safeToSearch = false;
    this.getWebContents();
  }
}
